/** Lokalna WYCENA z kompilatu taryfy FareLight (decyzja: silnik zostaje jeden —
 * ceny są PREKALKULOWANE per (relacja, oferta, kategoria); tutaj tylko dobór
 * kategorii pasażera, reguła składu i ważność). Odmowy zawsze Z POWODEM —
 * cicha nieobecność oferty jest nie do odróżnienia od awarii. */

// Typy reguł dopuszczalności sprzedaży offline mają JEDNĄ definicję (`policy.ts`)
// — druga, bliźniacza, rozjechałaby się przy pierwszej poprawce, a tu chodzi
// o pieniądze. Import jest TYPOWY, więc znika w czasie wykonania i nie wiąże
// silnika wyceny z modułem reguł.
import type { OfflinePolicy, SaleLimitRule } from "./policy";

export interface Passenger {
  age: number | null; category_codes: string[]; cards: string[];
  /** data urodzenia (RRRR-MM-DD) — pole NORMY. Przyjmujemy OBA zapisy, bo online
   * przyjmuje oba (`populate_by_name`): kasa mówi kształtem OSDM (`dateOfBirth`),
   * a nasz własny kontrakt jest snake_case. Jeden zapis znaczyłby, że ta sama
   * kasa dostaje ulgę online, a offline jedzie pełnopłatnie. */
  date_of_birth?: string | null;
  dateOfBirth?: string | null;
}

interface PriceCell {
  price_gr: number; category_name: string; source?: string;
  discount_pct?: number; category_discount_pct?: number;
  vat_percent?: number; netto_gr?: number; vat_gr?: number; display_note?: string;
}
interface OfferPrices { validity_hours: number | null; categories: Record<string, PriceCell> }
interface OfferMeta {
  code: string; name: string; local_offer_id: string | null; ticket_type: string;
  fare_type: string;
  issuing_rules: { min_persons?: number; max_persons?: number | null;
    max_discount_kinds?: number | null; allow_full_price?: boolean };
  eligibility: { mode?: string; age_min?: number | null; age_max?: number | null;
    required_codes?: string[]; required_cards?: string[] } | null;
  entitlement_offer: boolean; exclusive: boolean;
  train_numbers: string[]; sales_from: string | null; sales_until: string | null;
  validity_until_end_of_day: number | null; whole_ticket_validity_hours: number | null;
  available_in_channel: boolean; channel_reason: string | null;
  /** reguły honorowania PRZYPIĘTE do tej oferty (obok globalnych z kompilatu) */
  carrier_acceptances?: HonoringRule[];
}
/** Reguła honorowania rozwiązana przez kompilat: obszar → jawna lista stacji. */
export interface HonoringRule {
  carrier_rics: string; carrier_name: string | null;
  zone_ref: string | null; zone_uics: string[] | null;
  segment_pairs: string[]; train_numbers: string[];
  valid_from: string | null; valid_until: string | null; note: string | null;
}
/** Noga podróży: odcinek jednym pociągiem, z przewoźnikiem, który go wykonuje. */
export interface Leg {
  fromUic: string; toUic: string;
  carrierRics?: string | null; trainNumber?: string | null;
}
export interface HonouredLeg {
  fromUic: string; toUic: string; fromName: string | null; toName: string | null;
  carrierRics: string; carrierName: string | null;
  trainNumber: string | null; note: string | null;
}
interface Category {
  code: string; name: string; age_min?: number | null; age_max?: number | null;
  age_trigger?: boolean;
  /** czy ulgę zależną od wieku wolno przyznać BEZ podanego wieku — decyzja taryfy.
   * Kompilat liczy się w trybie SOFT (kanał kasy offline nie zaostrza), więc tu
   * rozstrzyga sama taryfa; `REFUSE` odmawia tak samo jak online. */
  age_unknown_policy?: "AS_DECLARED" | "REFUSE" | "GRANT_WITH_DOCUMENT";
  card_triggers?: { card_code: string; age_min?: number | null; age_max?: number | null }[];
  external_codes?: { code: string; scheme?: string; carrier_rics?: string | null;
    ticket_kind?: "SINGLE" | "MONTHLY" | null; train_category?: string | null;
    service_class?: string | null;
    /** reprezentant kodu dzielonego przez kilka kategorii (np. 99 = student
     * i doktorant) — wybór NAZWY na bilecie należy do taryfy, nie do kasy */
    primary?: boolean }[];
}
export interface Bundle {
  project: { id: string; name: string; carrier: Record<string, unknown> | null };
  stations: Record<string, { name: string; codes: Record<string, string> }>;
  relations: Record<string, number>;
  categories: Category[];
  offers: OfferMeta[];
  unsupported: { code: string; name: string; reason: string }[];
  // pary → {km, g: para reprezentatywna GRUPY CENOWEJ}; komórki w price_groups —
  // pary o tym samym km i zbiorze obowiązujących ofert dzielą jedną grupę
  pair_index: Record<string, { km: number; g: string }>;
  price_groups: Record<string, Record<string, OfferPrices>>;
  train_tariffs: { trains: string[]; prices_key: string }[];
  pair_index_by_trains: Record<string, Record<string, { km: number; g: string }>>;
  price_groups_by_trains: Record<string, Record<string, Record<string, OfferPrices>>>;
  /** honorowanie: reguły projektu (oferty mają swoje w `offers[].carrier_acceptances`) */
  carrier_acceptances?: HonoringRule[];
  /** graf odcinków BAZOWYCH (`pair_key` → metry) — do policzenia drogi nogi */
  base_edges?: Record<string, number>;
  /** progi sprzedaży bez sieci ustalone przez Zamawiającego (dziś: wiek pakietu).
   * BRAK pola = pakiet sprzed wprowadzenia progów — wtedy progu nie ma i kasa
   * zachowuje się jak dotąd, zamiast wstrzymać sprzedaż z powodu starych danych. */
  offline_policy?: OfflinePolicy | null;
  /** limity sprzedaży obowiązujące ten kanał (definicje, bez liczników —
   * licznik wspólny żyje na serwerze). BRAK pola = pakiet ich nie niesie. */
  sale_limits?: SaleLimitRule[] | null;
  bundle_hash: string;
  compiled_at: string;
}

export const BASE_CAT = "NORMALNY";
export const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

interface Line {
  passenger: number; category_code: string; category_name: string; price_gr: number;
  breakdown: Record<string, unknown> | null; red_code: number;
}
export interface TicketGroup {
  passengers: number[]; lines: Line[]; total_gr: number; discount_kinds: string[];
  nr_bil?: number; token?: string;
}
export interface OfferResult {
  code: string; name: string; localOfferId: string | null; ticketType: string;
  totalGr: number; lines: Line[]; tickets: TicketGroup[];
  validityHours: number | null; validityUntil: string | null;
  /** odcinki obcych przewoźników objęte tym biletem — idą na wydruk offline */
  honouredLegs: HonouredLeg[];
}
export interface QuoteOutput {
  offers: OfferResult[]; unavailable: { code: string; name: string; reason: string }[];
  distanceKm: number | null;
  /** pozycje, które podały datę urodzenia BEZ `age`, a wieku nie dało się z niej
   * wyprowadzić — odpowiednik `osdmIgnored.passengerDateOfBirth` online. Nie jest
   * to odmowa (oferta jest, tylko pełnopłatna), więc nie idzie do `unavailable`:
   * tamta lista odpowiada na „dlaczego nie ma oferty". */
  ageNotDerived: string[];
}

/** Dzień z zapisu daty, z kontrolą ISTNIENIA dnia.
 *
 * „2026-02-30" jest składniowo poprawne, a nie istnieje: `new Date` przewinąłby je
 * na 2 marca i dałby wiek o dzień inny niż online, gdzie `date.fromisoformat` taką
 * datę ODRZUCA. Parytet musi obejmować także odrzucenia, nie tylko wyniki. */
function parseDay(s: string): { y: number; m: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3];
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() + 1 !== mo || dt.getUTCDate() !== d)
    return null;
  return { y, m: mo, d };
}

/** Wiek z daty urodzenia na DZIEŃ PODRÓŻY. Zwraca pozycje nieprzeliczone.
 *
 * Przepisane z `_wiek_z_daty_urodzenia` w SaleLight — ta sama reguła, bo kasa
 * offline i sprzedaż online muszą dać ten sam wiek, a więc tę samą ulgę i ten
 * sam KOD ulgi w rekordzie rozliczeniowym.
 *
 * Dzień podróży, nie sprzedaży: ulga dotyczy przejazdu, a nie transakcji —
 * pasażer kończący 26 lat między zakupem a wyjazdem jedzie już bez ulgi
 * studenckiej. Bez daty podróży nie ma na czym liczyć i wtedy NIE zgadujemy.
 *
 * Podany `age` ma PIERWSZEŃSTWO: liczba jest deklaracją kasy wprost, a data
 * wymaga naszego przeliczenia, więc przy rozbieżności wygrywa to, co kasa
 * powiedziała sama.
 *
 * Mutuje listę w miejscu, tak jak online mutuje `body.passengers` — ta sama
 * lista idzie potem do ładunku sprzedaży, więc wiek na bilecie ma wynikać
 * z tego samego przeliczenia co cena. */
export function ageFromBirthDate(
  passengers: Passenger[], travelAt: string | null | undefined,
): string[] {
  // dzień AS WRITTEN, bez przeliczania na strefę urządzenia: wiek zmienia się
  // o północy KALENDARZOWEJ, więc strefa znacznika nie ma znaczenia. `new Date`
  // przeliczyłby znacznik tuż po północy na dzień wcześniejszy w innej strefie,
  // czyli na granicy progu dałby pasażera o rok młodszego niż online.
  const zapis = (travelAt ?? "").slice(0, 10);
  const dzien = /^\d{4}-\d{2}-\d{2}$/.test(zapis) ? parseDay(zapis) : null;
  const nieprzeliczone: string[] = [];
  passengers.forEach((p, i) => {
    if (p.age !== null && p.age !== undefined) return;
    const zapisUrodzin = p.date_of_birth ?? p.dateOfBirth ?? null;
    if (!zapisUrodzin) return;
    const etykieta = `#${i + 1}`;
    const urodziny = parseDay(String(zapisUrodzin).slice(0, 10));
    if (!urodziny || !dzien) { nieprzeliczone.push(etykieta); return; }
    const lata = dzien.y - urodziny.y
      - (dzien.m < urodziny.m || (dzien.m === urodziny.m && dzien.d < urodziny.d) ? 1 : 0);
    // data z przyszłości albo sprzed 150 lat to nie jest wiek, tylko literówka;
    // cicha korekta do zera dałaby ulgę dla dziecka
    if (lata >= 0 && lata <= 150) p.age = lata;
    else nieprzeliczone.push(etykieta);
  });
  return nieprzeliczone;
}

/** Rodzaj biletu na potrzeby KODU ulgi — odpowiednik ticket_kind_of w SaleLight. */
function ticketKindOf(ticketType: string | null | undefined): "SINGLE" | "MONTHLY" | null {
  if (ticketType === "MONTHLY_ONEWAY" || ticketType === "MONTHLY_RETURN" || ticketType === "PERIOD")
    return "MONTHLY";
  if (ticketType === "SINGLE" || ticketType === "RETURN" || ticketType === "CARNET")
    return "SINGLE";  // karnet = przejazdy jednorazowe
  return null;
}

function redCode(cat: Category | undefined, ticketType?: string | null,
                 trainCategory?: string | null, serviceClass?: string | null): number {
  // Ten sam priorytet co resolve_red_code w SaleLight: kod krajowy PL bez RICS,
  // a wpis ZAWĘŻONY do rodzaju biletu wygrywa z ogólnym. Jedno uprawnienie może
  // mieć dwa kody (uczniowie: 71 jednorazowo, 76 okresowo), więc bez rodzaju
  // biletu wybór byłby kolejnością wpisów — kwota poprawna, a w rozliczeniu inne
  // uprawnienie. Parytet offline/online musi obejmować także kod ulgi, nie tylko cenę.
  const kind = ticketKindOf(ticketType);
  const kandydaci = (cat?.external_codes ?? []).filter(
    (e) => (e.scheme || "PL").toUpperCase() === "PL" && !e.carrier_rics
      && (!e.ticket_kind || e.ticket_kind === kind)
      && (!e.train_category || e.train_category === trainCategory)
      && (!e.service_class || e.service_class === serviceClass),
  );
  // Kolejność osi jak w FareLighcie i SaleLighcie: klasa > kategoria pociągu > rodzaj.
  const waga = (e: { ticket_kind?: string | null; train_category?: string | null;
                     service_class?: string | null }) =>
    (e.service_class ? 4 : 0) + (e.train_category ? 2 : 0) + (e.ticket_kind ? 1 : 0);
  const wybrany = kandydaci.reduce<typeof kandydaci[number] | undefined>(
    (best, e) => (best === undefined || waga(e) > waga(best) ? e : best), undefined);
  // Kod ulgi pracowniczej UUT jest złożony („40:317" = ulga 40 u spółki 317);
  // RED_CODE niesie sam kod ulgi, inaczej parseInt dawałby NaN → 0.
  return wybrany ? parseInt(String(wybrany.code).split(":")[0], 10) || 0 : 0;
}

/** Kody zniżek podawane JAKO KARTY (dialekt kasy NKB):
 * `discount:statutory:<kod>` — ulga ustawowa, `discount:commercial:<kod>` — ulga
 * handlowa przewoźnika. To ten sam byt, co `external_codes` kategorii, więc
 * rozwiązujemy go po KODZIE, nigdy po wysokości ulgi (kod 37 wskazuje siedem
 * różnych uprawnień). Kasa offline musi rozstrzygać to TAK SAMO jak sprzedaż
 * online — inaczej ten sam pasażer dostaje tu inny kod w rekordzie FIN. */
const DISCOUNT_CARD = /^discount:(statutory|commercial):(.+)$/i;

export interface DiscountCardResolution {
  /** kody kategorii wynikające z podanych kodów zniżek */
  codes: string[];
  /** kody zniżek, których nie da się rozstrzygnąć — Z POWODEM dla kasjera */
  unresolved: { card: string; reason: string }[];
}

export function resolveDiscountCards(
  cards: string[], cats: Category[],
): DiscountCardResolution {
  const out: DiscountCardResolution = { codes: [], unresolved: [] };
  for (const card of cards) {
    const m = DISCOUNT_CARD.exec(card);
    if (!m) continue;  // zwykła karta zniżkowa (IR, KDR) — nie ten mechanizm
    const ustawowa = m[1].toLowerCase() === "statutory";
    // Kod złożony ulgi pracowniczej („40:317" = ulga 40 u spółki 317) próbujemy
    // najpierw W CAŁOŚCI — przewoźnik może mieć kod z dwukropkiem — a dopiero
    // potem bez członu spółki; ta sama kolejność, co w SaleLighcie.
    const szukane = [m[2], m[2].split(":")[0]];
    const pasuje = (c: Category) => (c.external_codes ?? []).some((e) => {
      const wlasny = Boolean(e.carrier_rics) || (e.scheme ?? "PL").toUpperCase() !== "PL";
      return ustawowa !== wlasny && szukane.includes(String(e.code));
    });
    const kandydaci = cats.filter(pasuje);
    if (!kandydaci.length) {
      // Przemilczana ulga to bilet pełnopłatny, a przy obcym kanale reklamacja
      // nie do odtworzenia — dlatego nieznany kod jest ODMOWĄ, jak online.
      out.unresolved.push({ card,
        reason: `nieznany kod zniżki „${m[2]}” (${ustawowa ? "ustawowa" : "handlowa"})` });
      continue;
    }
    if (kandydaci.length === 1) { out.codes.push(kandydaci[0].code); continue; }
    // Uprawnienie DOMYŚLNE wskazuje taryfa (`external_codes[].primary`) — tak samo
    // jak online. DWA wskazane dla tego samego kodu to sprzeczność w taryfie i
    // zostaje odmowa: wzięcie pierwszego byłoby zgadywaniem, a kasa offline musi
    // wybrać TEN SAM kod co sprzedaż online, nie „jakiś”.
    const glowni = kandydaci.filter(
      (c) => (c.external_codes ?? []).some((e) => e.primary && szukane.includes(String(e.code))));
    if (glowni.length === 1) { out.codes.push(glowni[0].code); continue; }
    out.unresolved.push({ card,
      reason: glowni.length > 1
        ? `kod zniżki „${m[2]}” ma w taryfie DWA uprawnienia domyślne `
          + `(${glowni.map((c) => c.code).join(", ")}) — to sprzeczność do poprawienia w taryfie`
        : `kod zniżki „${m[2]}” wskazuje kilka kategorii `
          + `(${kandydaci.map((c) => c.code).join(", ")}) i taryfa nie wskazała `
          + "uprawnienia domyślnego — podaj kategorię wprost" });
  }
  return out;
}

/** Kategorie przysługujące pasażerowi — deklaracja, trigger karty, trigger wieku.
 *
 * MUSI dawać ten sam wynik co `_eligible` w silniku online (`quote.py`): bilet
 * sprzedany w kasie offline i ten sam bilet sprzedany online mają być tym samym
 * produktem w tej samej cenie. Dwie reguły są więc przepisane stamtąd dosłownie:
 * wiek sprzeczny z progiem NIE odbiera ulgi wskazanej kodem ani kartą, bo w trybie
 * deklaratywnym nikt go tu nie weryfikuje — jest DANĄ pasażera, nie dowodem
 * uprawnienia; odbiera ją wyłącznie zakaz TARYFY (`age_unknown_policy: REFUSE`),
 * który obowiązuje w obu trybach. Ulgę z samego wieku (`age_trigger`) rozstrzyga
 * wiek, bo tam nie ma czyjej deklaracji przyjąć. Kompilat powstaje
 * w trybie SOFT, bo kasa offline nie zna ustawienia kanału — kanał z weryfikacją
 * uprawnień (HARD) obsługiwany jest wyłącznie online. */
function eligibleCats(p: Passenger, cats: Category[]): Set<string> {
  const out = new Set<string>([BASE_CAT]);
  const poKodzie = new Map(cats.map((c) => [c.code, c]));
  const inAge = (min?: number | null, max?: number | null) =>
    p.age !== null && (min == null || p.age >= min) && (max == null || p.age <= max);
  const hasBounds = (min?: number | null, max?: number | null) => min != null || max != null;
  const bezWiekuOk = (c: Category) => (c.age_unknown_policy ?? "AS_DECLARED") !== "REFUSE";

  // Ulga WSKAZANA KODEM: kompilat liczy się w trybie DEKLARATYWNYM, więc wskazanie
  // kasjera przyjmujemy — wiek jest tu daną pasażera, nie dowodem uprawnienia.
  // Ulgi nie ma tylko wtedy, gdy zabrania jej TARYFA (`REFUSE`) i wiek tego nie
  // potwierdza; zakaz przewoźnika obowiązuje w obu trybach.
  for (const kod of p.category_codes) {
    const c = poKodzie.get(kod);
    // kategoria spoza kompilatu zostaje jak dotąd (nie mamy czym jej sprawdzić)
    if (!c) { out.add(kod); continue; }
    if (hasBounds(c.age_min, c.age_max)
        && !inAge(c.age_min, c.age_max) && !bezWiekuOk(c)) continue;
    out.add(kod);
  }
  for (const c of cats) {
    // KARTA OKAZANA — jak wyżej: dokument jest wskazaniem, nie wiek
    for (const t of c.card_triggers ?? []) {
      if (!p.cards.includes(t.card_code)) continue;
      if (hasBounds(t.age_min, t.age_max)
          && !inAge(t.age_min, t.age_max) && !bezWiekuOk(c)) continue;
      out.add(c.code);
    }
    // SAM WIEK (`age_trigger`): nikt niczego nie wskazał, więc nie ma czyjej
    // deklaracji przyjąć — poza wyjątkiem, który taryfa nazywa wprost
    if (c.age_trigger && hasBounds(c.age_min, c.age_max)) {
      if (inAge(c.age_min, c.age_max)) out.add(c.code);
      else if (p.age === null && c.age_unknown_policy === "GRANT_WITH_DOCUMENT")
        out.add(c.code);
    }
  }
  return out;
}

function offerEligible(o: OfferMeta, p: Passenger): boolean {
  const e = o.eligibility;
  if (!e) return true;
  const checks: boolean[] = [];
  if (e.age_min != null || e.age_max != null)
    checks.push(p.age !== null && (e.age_min == null || p.age >= e.age_min)
      && (e.age_max == null || p.age <= e.age_max));
  if (e.required_codes?.length)
    checks.push(e.required_codes.some((c) => p.category_codes.includes(c)));
  if (e.required_cards?.length)
    checks.push(e.required_cards.some((c) => p.cards.includes(c)));
  if (!checks.length) return true;
  return (e.mode || "ALL") === "ALL" ? checks.every(Boolean) : checks.some(Boolean);
}

/** Reguła składu — podział pasażerów na bilety wg limitów oferty (jak TicketGroup
 * FareLight): ≤ max_persons osób i ≤ max_discount_kinds RÓŻNYCH zniżek na bilecie;
 * allow_full_price — pełnopłatni nie liczą się do rodzajów zniżek. */
function splitTickets(lines: Line[], rules: OfferMeta["issuing_rules"]): TicketGroup[] {
  const maxPersons = rules.max_persons ?? null;
  const maxKinds = rules.max_discount_kinds ?? null;
  const groups: { lines: Line[]; kinds: Set<string> }[] = [];
  for (const ln of lines) {
    const kind = ln.category_code === BASE_CAT ? null : ln.category_code;
    let target = groups.find((g) =>
      (maxPersons == null || g.lines.length < maxPersons)
      && (kind === null
        ? rules.allow_full_price !== false || g.kinds.size === 0
        : g.kinds.has(kind) || maxKinds == null || g.kinds.size < maxKinds));
    if (!target) { target = { lines: [], kinds: new Set() }; groups.push(target); }
    target.lines.push(ln);
    if (kind !== null) target.kinds.add(kind);
  }
  return groups.map((g) => ({
    passengers: g.lines.map((l) => l.passenger),
    lines: g.lines,
    total_gr: g.lines.reduce((s, l) => s + l.price_gr, 0),
    discount_kinds: [...g.kinds],
  }));
}

/** Droga nogi po odcinkach BAZOWYCH (Dijkstra) — ta sama, po której liczy online
 * `_shortest_base_path`. Bez niej nie da się odróżnić honorowania „na odcinku X–Y"
 * od honorowania wszędzie; z samych końców nogi to nie wynika. */
function basePath(edges: Record<string, number>, from: string, to: string): string[] | null {
  if (from === to) return [from];
  const adj = new Map<string, [string, number][]>();
  for (const [key, m] of Object.entries(edges)) {
    const [a, b] = key.split("|");
    (adj.get(a) ?? adj.set(a, []).get(a)!).push([b, m]);
    (adj.get(b) ?? adj.set(b, []).get(b)!).push([a, m]);
  }
  const dist = new Map<string, number>([[from, 0]]);
  const prev = new Map<string, string>();
  const seen = new Set<string>();
  for (;;) {
    let u: string | null = null;
    for (const [n, d] of dist) if (!seen.has(n) && (u === null || d < dist.get(u)!)) u = n;
    if (u === null) return null;
    if (u === to) break;
    seen.add(u);
    for (const [v, m] of adj.get(u) ?? []) {
      const nd = dist.get(u)! + m;
      if (!dist.has(v) || nd < dist.get(v)!) { dist.set(v, nd); prev.set(v, u); }
    }
  }
  const path = [to];
  while (path[0] !== from) path.unshift(prev.get(path[0])!);
  return path;
}

/** Czy reguła obejmuje CAŁĄ drogę nogi. „Do stacji X" nie uprawnia do jazdy dalej —
 * ta sama zasada, co w `CarrierAcceptance.covers_path` online. */
function ruleCoversLeg(rule: HonoringRule, path: string[], leg: Leg, travelAt: Date | null): boolean {
  if (rule.valid_from && (!travelAt || travelAt < new Date(rule.valid_from))) return false;
  if (rule.valid_until && (!travelAt || travelAt > new Date(rule.valid_until))) return false;
  // kurs NIEZNANY nie spełnia warunku kursowego — uprawnienia nie zgadujemy
  if (rule.train_numbers.length
      && !(leg.trainNumber && rule.train_numbers.includes(leg.trainNumber))) return false;
  if (rule.zone_uics && !path.every((u) => rule.zone_uics!.includes(u))) return false;
  if (rule.segment_pairs.length) {
    for (let i = 0; i + 1 < path.length; i++)
      if (!rule.segment_pairs.includes(pairKey(path[i], path[i + 1]))) return false;
  }
  return true;
}

/** Nogi obcych przewoźników: [powód odmowy, odcinki objęte honorowaniem].
 * Przewoźnik nogi NIEZNANY = przewoźnik taryfy (fail-open, jak online: odmowa
 * z powodu niewiedzy zablokowałaby sprzedaż tam, gdzie wszystko jest w porządku). */
function foreignLegs(
  bundle: Bundle, meta: OfferMeta, legs: Leg[], travelAt: Date | null,
): [string | null, HonouredLeg[]] {
  const ourRics = String((bundle.project.carrier as { rics?: string } | null)?.rics ?? "");
  const foreign = legs.filter((l) => l.carrierRics && l.carrierRics !== ourRics);
  if (!foreign.length) return [null, []];
  const rules = [...(bundle.carrier_acceptances ?? []), ...(meta.carrier_acceptances ?? [])];
  const nazwa = (u: string) => bundle.stations[u]?.name ?? u;
  const out: HonouredLeg[] = [];
  for (const leg of foreign) {
    const path = basePath(bundle.base_edges ?? {}, leg.fromUic, leg.toUic)
      ?? [leg.fromUic, leg.toUic];
    const hit = rules.find(
      (r) => r.carrier_rics === leg.carrierRics && ruleCoversLeg(r, path, leg, travelAt));
    if (!hit) {
      const kto = rules.find((r) => r.carrier_rics === leg.carrierRics)?.carrier_name
        ?? `przewoźnik ${leg.carrierRics}`;
      const kurs = leg.trainNumber ? ` (kurs ${leg.trainNumber})` : "";
      return [`odcinek ${nazwa(leg.fromUic)} – ${nazwa(leg.toUic)}${kurs} wykonuje ${kto}, `
        + "który nie honoruje biletów tej oferty na tym odcinku — na tę część trasy "
        + "potrzebny jest jego bilet", []];
    }
    out.push({
      fromUic: leg.fromUic, toUic: leg.toUic,
      fromName: nazwa(leg.fromUic), toName: nazwa(leg.toUic),
      carrierRics: leg.carrierRics!, carrierName: hit.carrier_name,
      trainNumber: leg.trainNumber ?? null, note: hit.note,
    });
  }
  return [null, out];
}

export function quoteOffline(
  bundle: Bundle,
  args: { fromUic: string; toUic: string; trainNumber?: string | null;
    passengers: Passenger[]; saleAt: Date; travelAt?: string | null;
    /** nogi z przewoźnikami — włączają kontrolę honorowania (pusto = własne pociągi) */
    legs?: Leg[];
    /** klasa — jedna z osi, po których zmienia się KOD ulgi (obok kategorii pociągu) */
    serviceClass?: string | null },
): QuoteOutput {
  // Wiek z daty urodzenia liczymy TUTAJ, w silniku, a nie u wołającego: każda
  // wycena offline przechodzi tędy, więc kolejna ścieżka wywołania dostaje regułę
  // za darmo. Musi to być PRZED sprowadzeniem kart do kategorii, bo tamten krok
  // kopiuje obiekty pasażerów i kopia nie zobaczyłaby uzupełnionego wieku.
  const ageNotDerived = ageFromBirthDate(args.passengers, args.travelAt);
  const pair = pairKey(args.fromUic, args.toUic);
  const unavailable: QuoteOutput["unavailable"] = [
    ...bundle.unsupported.map((u) => ({ ...u, reason: `${u.reason} — offline` })),
  ];
  // blok cen: taryfa kursowa (numer kursu w zestawie) albo ogólny; potem dwa
  // skoki: pair_index[pair].g → price_groups[g]
  let index = bundle.pair_index;
  let groups = bundle.price_groups;
  if (args.trainNumber) {
    const hit = bundle.train_tariffs.find((t) => t.trains.includes(args.trainNumber!));
    if (hit) {
      index = bundle.pair_index_by_trains[hit.prices_key];
      groups = bundle.price_groups_by_trains[hit.prices_key];
    }
  }
  const entry = index[pair];
  const cells = entry ? groups[entry.g] : undefined;
  if (!cells) {
    return { offers: [], distanceKm: entry?.km ?? bundle.relations[pair] ?? null,
      unavailable: [{ code: "-", name: "-",
        reason: "relacja poza kompilatem taryfy — wycena offline niemożliwa" }],
      ageNotDerived };
  }
  const catsByCode = new Map(bundle.categories.map((c) => [c.code, c]));
  const saleDay = args.saleAt.toISOString().slice(0, 10);
  const out: OfferResult[] = [];

  // Kody zniżek podane jako karty (`discount:statutory:99`) sprowadzamy do
  // kategorii JEDEN RAZ, przed wyceną ofert: nierozstrzygnięty kod nie może
  // skończyć się cichą sprzedażą pełnopłatną, bo pasażer okazał uprawnienie.
  const pasazerowie: Passenger[] = [];
  const nierozstrzygniete: string[] = [];
  for (const p of args.passengers) {
    const r = resolveDiscountCards(p.cards, bundle.categories);
    for (const u of r.unresolved) nierozstrzygniete.push(u.reason);
    pasazerowie.push(r.codes.length
      ? { ...p, category_codes: [...p.category_codes, ...r.codes] } : p);
  }
  if (nierozstrzygniete.length)
    return { offers: [], distanceKm: entry.km,
      unavailable: [{ code: "-", name: "-", reason: nierozstrzygniete.join("; ") }],
      ageNotDerived };

  for (const meta of bundle.offers) {
    const priced = cells[meta.code];
    const refuse = (reason: string) =>
      unavailable.push({ code: meta.code, name: meta.name, reason });
    if (!priced) continue; // zasięg rozstrzygnął kompilat (obszar/wyłączność)
    if (!meta.available_in_channel) { refuse(meta.channel_reason || "poza kanałem"); continue; }
    if (meta.sales_from && saleDay < meta.sales_from) {
      refuse(`sprzedaż oferty od ${meta.sales_from}`); continue;
    }
    if (meta.sales_until && saleDay > meta.sales_until) {
      refuse(`sprzedaż oferty do ${meta.sales_until}`); continue;
    }
    // przejazd kilku przewoźników na JEDNYM bilecie — tylko tam, gdzie tamten
    // przewoźnik honoruje bilety tej oferty (reguły przyszły w kompilacie)
    const [obcy, honoured] = foreignLegs(
      bundle, meta, args.legs ?? [], args.travelAt ? new Date(args.travelAt) : null);
    if (obcy) { refuse(obcy); continue; }
    const lines: Line[] = [];
    let ok = true;
    for (let i = 0; i < pasazerowie.length; i++) {
      const p = pasazerowie[i];
      if (!offerEligible(meta, p)) { ok = false; refuse("pasażer nie spełnia warunków oferty"); break; }
      const eligible = eligibleCats(p, bundle.categories);
      let best: { cell: PriceCell; code: string } | null = null;
      for (const [code, cell] of Object.entries(priced.categories)) {
        if (!eligible.has(code)) continue;
        if (!best || cell.price_gr < best.cell.price_gr) best = { cell, code };
      }
      if (!best) { ok = false; refuse(`pasażer ${i + 1}: brak przysługującej kategorii`); break; }
      lines.push({
        passenger: i + 1, category_code: best.code,
        category_name: best.cell.category_name, price_gr: best.cell.price_gr,
        breakdown: {
          source: best.cell.source ?? null, discount_pct: best.cell.discount_pct ?? 0,
          category_discount_pct: best.cell.category_discount_pct ?? 0,
          vat_percent: best.cell.vat_percent ?? null,
          netto_gr: best.cell.netto_gr ?? null, vat_gr: best.cell.vat_gr ?? null,
        },
        // Kategorii pociągu kompilat nie niesie (ma numery kursów, nie kategorie),
        // więc kod zawężony do kategorii offline nie zadziała — i lepiej, żeby nie
        // zadziałał, niż żeby offline wybrał inny kod niż sprzedaż online.
        red_code: redCode(catsByCode.get(best.code), meta.ticket_type,
                          null, args.serviceClass ?? null),
      });
    }
    if (!ok) continue;
    const min = meta.issuing_rules.min_persons ?? 1;
    if (pasazerowie.length < min) { refuse(`oferta wymaga min. ${min} osób`); continue; }
    // ważność: kalendarzowa (do końca doby+N) bije godzinową; godzinowa z komórki
    let validityUntil: string | null = null;
    if (args.travelAt) {
      const start = new Date(args.travelAt);
      if (meta.validity_until_end_of_day != null) {
        const end = new Date(start);
        end.setDate(end.getDate() + meta.validity_until_end_of_day);
        end.setHours(23, 59, 59, 0);
        validityUntil = end.toISOString();
      } else {
        const hours = meta.whole_ticket_validity_hours ?? priced.validity_hours;
        if (hours != null)
          validityUntil = new Date(start.getTime() + hours * 3_600_000).toISOString();
      }
    }
    out.push({
      code: meta.code, name: meta.name, localOfferId: meta.local_offer_id,
      ticketType: meta.ticket_type,
      totalGr: lines.reduce((s, l) => s + l.price_gr, 0),
      lines, tickets: splitTickets(lines, meta.issuing_rules),
      validityHours: priced.validity_hours, validityUntil,
      honouredLegs: honoured,
    });
  }
  out.sort((a, b) => a.totalGr - b.totalGr);
  return { offers: out, unavailable, distanceKm: entry.km, ageNotDerived };
}
